import * as XLSX from "xlsx";

export const TEMPLATE_HEADERS = [
  "First name",
  "Surname",
  "Membership number",
  "Email",
  "Phone",
  "Category",
  "Organization",
  "Status",
  "Approval",
  "Certificate",
  "Payment status",
  "Amount",
  "Payment method",
  "Payment reference",
  "Date paid",
];

const norm = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const normNumber = (n) =>
  String(n ?? "")
    .trim()
    .toUpperCase()
    .replace(/^NSK\s*-?\s*/i, "")
    .replace(/\s+/g, "");

/* order matters: more specific payment columns first */
const FIELD_RULES = [
  ["amount", (h) => h.includes("amount") || h === "fee" || h === "fees"],
  ["method", (h) => h.includes("method") || h === "mode" || h === "paymentmode"],
  ["reference", (h) => h.includes("reference") || h.includes("receipt") || h.includes("transaction") || h === "ref" || h === "mpesacode"],
  ["paidOn", (h) => ["datepaid", "paidon", "paymentdate", "dateofpayment", "datepaidon", "paiddate"].includes(h)],
  ["payStatus", (h) => h.startsWith("payment") || h.startsWith("paid") || h === "feestatus"],
  ["membershipNumber", (h) => ["membershipnumber", "membershipno", "membernumber", "memberno", "membershipid", "memberid", "nskno", "nsknumber", "membershipnum"].includes(h)],
  ["firstName", (h) => ["firstname", "first", "givenname", "forename"].includes(h)],
  ["lastName", (h) => ["lastname", "secondname", "surname", "familyname"].includes(h)],
  ["fullName", (h) => ["fullname", "name", "membername", "member", "names"].includes(h)],
  ["email", (h) => h.includes("email") || h === "mail"],
  ["phone", (h) => h.includes("phone") || h.includes("mobile") || h.startsWith("tel") || h === "contact"],
  ["category", (h) => ["category", "membershiptype", "membershipcategory", "membercategory", "type"].includes(h)],
  ["organization", (h) => ["organization", "organisation", "institution", "company", "employer", "affiliation"].includes(h)],
  ["status", (h) => ["status", "memberstatus", "membershipstatus", "active"].includes(h)],
  ["approval", (h) => h.includes("approval")],
  ["certificate", (h) => h.includes("certificate")],
];

export const FIELD_LABELS = {
  firstName: "First name",
  lastName: "Surname",
  fullName: "Full name",
  membershipNumber: "Membership number",
  email: "Email",
  phone: "Phone",
  category: "Category",
  organization: "Organization",
  status: "Status",
  approval: "Approval",
  certificate: "Certificate",
  payStatus: "Payment status",
  amount: "Amount",
  method: "Payment method",
  reference: "Payment reference",
  paidOn: "Date paid",
};

/* small value helpers */

const pad = (n) => String(n).padStart(2, "0");

function ymd(d) {
  const x = new Date(d.getTime() + 12 * 3600 * 1000);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}

function parseDate(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? "" : ymd(v);
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/); // day first (Kenya)
  if (m) {
    let y = Number(m[3]);
    if (y < 100) y += 2000;
    return `${y}-${pad(Number(m[2]))}-${pad(Number(m[1]))}`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : ymd(d);
}

function cellText(v) {
  if (v instanceof Date) return ymd(v);
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function fixPhone(p) {
  return /^[71]\d{8}$/.test(p) ? `0${p}` : p;
}

function splitName(full) {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") };
}

const YES = ["paid", "yes", "y", "true", "1", "x", "✓", "✔", "settled", "complete", "completed"];
const NO = ["unpaid", "no", "n", "false", "0", "notpaid", "pending", "owing", "outstanding", "due"];

function parsePayStatus(v) {
  const s = norm(v);
  if (!s) return "";
  if (YES.includes(s)) return "paid";
  if (NO.includes(s)) return "unpaid";
  return "";
}

function matchCategory(raw, categories) {
  if (!raw) return { value: "" };
  const n = norm(raw);
  const hit =
    categories.find((c) => norm(c) === n) ||
    categories.find((c) => norm(c).replace("member", "") === n.replace("member", "")) ||
    categories.find((c) => norm(c).startsWith(n));
  return hit ? { value: hit } : { value: raw, unknown: true };
}

function parseApproval(v) {
  const s = norm(v);
  if (["approved", "approve", "yes", "y"].includes(s)) return "Approved";
  if (["pending", "awaiting", "inreview"].includes(s)) return "Pending";
  if (["rejected", "declined", "denied"].includes(s)) return "Rejected";
  return "";
}

function parseCertificate(v) {
  const s = norm(v);
  if (["issued", "yes", "y", "true", "given", "collected"].includes(s)) return "Issued";
  if (["no", "n", "notissued", "notrecorded", "false"].includes(s)) return "Not recorded";
  return "";
}

function parseStatus(v) {
  const s = norm(v);
  if (["active", "yes", "y", "current", "paidup"].includes(s)) return "Active";
  if (["inactive", "lapsed", "expired", "suspended", "no", "n", "dormant"].includes(s)) return "Inactive";
  return "";
}

/* reading the workbook */

export function parseWorkbook(buffer) {
  const wb = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error("The file has no sheets.");
  const grid = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
    header: 1,
    defval: "",
    raw: true,
    blankrows: true,
  });
  if (!grid.length) throw new Error("The first sheet is empty.");

  const headers = grid[0].map((h) => String(h ?? "").trim());
  const mapping = {};
  const unmapped = [];
  headers.forEach((h, idx) => {
    if (!h) return;
    const n = norm(h);
    const rule = FIELD_RULES.find(([field, test]) => mapping[field] === undefined && test(n));
    if (rule) mapping[rule[0]] = idx;
    else unmapped.push(h);
  });

  const hasName = mapping.firstName !== undefined || mapping.lastName !== undefined || mapping.fullName !== undefined;
  if (!hasName) {
    throw new Error(
      "Couldn't find a name column. Add a header such as “First name” and “Surname” (or “Full name”) in the first row."
    );
  }

  return {
    sheetName,
    headers,
    mapping,
    unmapped,
    rows: grid.slice(1).map((cells, i) => ({ rowNum: i + 2, cells })),
  };
}

/* building the preview plan */

export function buildPlan(parsed, { members, payments, categories, updateExisting, newStatus }) {
  const byNumber = new Map();
  const byEmail = new Map();
  const byName = new Map();
  members.forEach((m) => {
    const n = normNumber(m.memberNumber);
    if (n) byNumber.set(n, m);
    if (m.email) byEmail.set(m.email.toLowerCase(), m);
    byName.set(m.fullName.toLowerCase(), m);
  });

  const m = parsed.mapping;
  const raw = (cells, field) => (m[field] === undefined ? "" : cells[m[field]]);
  const get = (cells, field) => cellText(raw(cells, field));

  const seen = new Set();
  const items = [];
  const unknownCategories = new Set();

  parsed.rows.forEach(({ rowNum, cells }) => {
    if (cells.every((c) => cellText(c) === "")) return; 

    let first = get(cells, "firstName");
    let last = get(cells, "lastName");
    const full = get(cells, "fullName");
    if (!first && !last && full) ({ first, last } = splitName(full));

    const number = get(cells, "membershipNumber");
    const email = get(cells, "email");
    const phone = fixPhone(get(cells, "phone"));
    const displayName = [first, last].filter(Boolean).join(" ");
    const notes = [];

    const existing =
      (number && byNumber.get(normNumber(number))) ||
      (email && byEmail.get(email.toLowerCase())) ||
      null;

    const base = { rowNum, name: displayName || "(no name)", number, email, notes, data: {}, payment: null, match: existing };

    const dupKey = existing
      ? `id:${existing.id}`
      : normNumber(number)
      ? `no:${normNumber(number)}`
      : email
      ? `em:${email.toLowerCase()}`
      : `nm:${displayName.toLowerCase()}`;
    if (seen.has(dupKey)) {
      items.push({ ...base, action: "skip", reason: "Duplicate of an earlier row in this file" });
      return;
    }
    seen.add(dupKey);

    /* normalised member fields from this row (only non-empty ones) */
    const cat = matchCategory(get(cells, "category"), categories);
    if (cat.unknown) {
      unknownCategories.add(cat.value);
      notes.push(`Unrecognised category “${cat.value}”`);
    }
    const surnameField = existing ? existing.surnameField : "lastName";
    const fields = {};
    if (first) fields.firstName = first;
    if (last) fields[surnameField] = last;
    if (number) fields.membershipNumber = number;
    if (email) fields.email = email;
    if (phone) fields.phone = phone;
    if (cat.value) fields.category = cat.value;
    if (existing && number && normNumber(number) === normNumber(existing.memberNumber)) {
      delete fields.membershipNumber;
    }
    const org = get(cells, "organization");
    if (org) fields.organization = org;
    const approval = parseApproval(get(cells, "approval"));
    if (approval) fields.approval = approval;
    const status = parseStatus(get(cells, "status"));
    if (status) fields.status = status;
    const certificate = parseCertificate(get(cells, "certificate"));
    if (certificate) fields.certificate = certificate;

    /* payment for the selected period */
    const amountText = get(cells, "amount").replace(/[^0-9.]/g, "");
    const amount = amountText ? Number(amountText) : 0;
    let payStatus = parsePayStatus(get(cells, "payStatus"));
    if (!payStatus && amount > 0) payStatus = "paid";
    const rawDate = raw(cells, "paidOn");
    const paidOn = parseDate(rawDate);
    if (cellText(rawDate) && !paidOn) notes.push("Couldn't read the payment date");
    let payment = null;
    if (payStatus) {
      payment = { status: payStatus };
      if (amount > 0) payment.amount = amount;
      const method = get(cells, "method");
      if (method) payment.method = method;
      const reference = get(cells, "reference");
      if (reference) payment.reference = reference;
      if (paidOn) payment.paidOn = paidOn;
    } else if (get(cells, "payStatus")) {
      notes.push(`Payment status “${get(cells, "payStatus")}” not understood (use Paid / Unpaid)`);
    }

    if (existing) {
      if (!updateExisting) {
        items.push({ ...base, action: "skip", reason: "Already on the register" });
        return;
      }
   
      const current = {
        firstName: existing.firstName,
        [surnameField]: existing.lastName,
        membershipNumber: existing.memberNumber,
        email: existing.email,
        phone: existing.phone,
        category: existing.category,
        organization: existing.organization,
        approval: existing.approval,
        status: existing.status,
        certificate: existing.certificate,
      };
      const changed = {};
      Object.entries(fields).forEach(([k, v]) => {
        if ((current[k] ?? "") !== v) changed[k] = v;
      });
      const prev = payments[existing.id];
      const payChanged =
        payment &&
        (!prev ||
          prev.status !== payment.status ||
          (payment.amount !== undefined && Number(prev.amount) !== payment.amount) ||
          (payment.method !== undefined && prev.method !== payment.method) ||
          (payment.reference !== undefined && prev.reference !== payment.reference) ||
          (payment.paidOn !== undefined && prev.paidOn !== payment.paidOn));

      if (!Object.keys(changed).length && !payChanged) {
        items.push({ ...base, name: existing.fullName, action: "skip", reason: "Already up to date" });
        return;
      }
      items.push({
        ...base,
        name: existing.fullName,
        action: "update",
        data: changed,
        payment: payChanged ? payment : null,
        changedKeys: [...Object.keys(changed), ...(payChanged ? ["payment"] : [])],
      });
      return;
    }

    /* new member */
    if (!displayName) {
      items.push({ ...base, action: "skip", reason: "No name in this row" });
      return;
    }
    const sameName = byName.get(displayName.toLowerCase());
    if (sameName) {
      items.push({
        ...base,
        action: "skip",
        reason: "Same name is already on the register. Add the membership number or email to update them",
      });
      return;
    }
    items.push({
      ...base,
      action: "create",
      data: {
        firstName: first,
        [surnameField]: last,
        membershipNumber: number,
        email,
        phone,
        category: cat.value,
        organization: org,
        approval: approval || "Not recorded",
        status: status || newStatus,
        certificate: certificate || "Not recorded",
      },
      payment,
      memberName: displayName,
    });
  });

  const counts = {
    create: items.filter((i) => i.action === "create").length,
    update: items.filter((i) => i.action === "update").length,
    skip: items.filter((i) => i.action === "skip").length,
    payments: items.filter((i) => i.action !== "skip" && i.payment).length,
  };

  return {
    items,
    counts,
    hasPaymentColumns: m.payStatus !== undefined || m.amount !== undefined,
    unknownCategories: [...unknownCategories],
  };
}

/* downloadable template */

export function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const rows = [
    TEMPLATE_HEADERS,
    ["Example", "Member One", "NSK-EX1", "example1@example.com", "0712345678", "Regular Member", "Example Institute", "Active", "Approved", "Issued", "Paid", 2000, "M-Pesa", "ABC123XYZ", "2026-01-15"],
    ["Example", "Member Two", "NSK-EX2", "example2@example.com", "0722345678", "Student Member", "Example University", "Active", "Pending", "", "Unpaid", "", "", "", ""],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(14, h.length + 2) }));
  XLSX.utils.book_append_sheet(wb, ws, "Members");

  const help = XLSX.utils.aoa_to_sheet([
    ["How to fill this sheet"],
    ["Delete the two example rows before importing."],
    ["Only a name is required. Every other column is optional. You can leave columns out entirely."],
    ["Members are matched to the register by Membership number, then by Email. Matches are updated, others are added."],
    ["Empty cells never erase information that is already saved."],
    ["Category: Regular Member, Associate Member, Student Member, Affiliate Member or Corporate Member."],
    ["Status: Active or Inactive. Only Active members appear in the public verification form."],
    ["Approval: Approved, Pending or Rejected.  Certificate: Issued or leave empty."],
    ["Payment status: Paid or Unpaid. It is saved for the membership period selected on the register page."],
    ["Date paid: write dates like 2026-01-15 or 15/01/2026."],
  ]);
  help["!cols"] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(wb, help, "How to fill");

  XLSX.writeFile(wb, "NSK-members-import-template.xlsx");
}
